import { Currency, Formula } from "@inrev/common";
import { NumberParseError, Result } from "@inrev/core";

const CURRENCY_NOISE = /[$,\s]/g;
// A whole value wrapped in parens, e.g. accounting-negative `(500)`.
const PARENTHESIZED = /^\((.*)\)$/;
// A lone (optionally negative) numeric literal — no internal operators. The
// accounting-negative `(500)` → `-500` transform produces one of these.
const PLAIN_NUMBER = /^-?[0-9.]+$/;
// Any character outside the inline-formula grammar (`+ - * / ( )` and numbers).
// Rejects identifiers up front so the Formula engine never binds `g`.
const NON_FORMULA_CHAR = /[^0-9.+\-*/()]/;

const NOT_A_NUMBER_MESSAGE = "Cell value is not a valid number";

const parsePlainNumber = (cleaned: string): Result<NumberParseError, number> => {
	const n = Number(cleaned);
	return Number.isFinite(n) ? Result.ok(n) : Result.err(new NumberParseError(NOT_A_NUMBER_MESSAGE));
};

export const parseDraftToNumber = (draft: string): Result<NumberParseError, number | null> => {
	const cleaned = draft.replace(CURRENCY_NOISE, "");

	// Accounting negatives keep their plain-number semantics: `(500)` → `-500`,
	// `()` → null, `(-5)` → the invalid `--5`. Wrapped values never fall through
	// to formula evaluation.
	const accountingNegative = PARENTHESIZED.exec(cleaned);
	if (accountingNegative) {
		const inner = `-${accountingNegative[1]}`;
		return inner === "-" ? Result.ok(null) : parsePlainNumber(inner);
	}

	if (cleaned === "" || cleaned === "-") return Result.ok(null);
	if (PLAIN_NUMBER.test(cleaned)) return parsePlainNumber(cleaned);
	if (NON_FORMULA_CHAR.test(cleaned))
		return Result.err(new NumberParseError("Cell value may only contain numbers and + - * /"));

	// Evaluate the inline formula and store only the resolved number. `g` is bound
	// to zero but can never appear (rejected above), so the base value is unused.
	return Formula.parse(cleaned)
		.badMap((error) => new NumberParseError(error.message))
		.flatMap((formula) => {
			const value = Formula.evaluate(Currency.ZERO, formula);
			return Number.isFinite(value)
				? Result.ok(Currency.round(value))
				: Result.err(new NumberParseError(NOT_A_NUMBER_MESSAGE));
		});
};
